/**
 * Crew: the roster, and the audit trail behind it.
 *
 * The tab, the tile filter and the search term all live in the URL. A site
 * manager who has filtered to "missing / expired" and searched a name is
 * looking at something worth sending to someone, and a link that reopens a
 * different view is a link that gets someone the wrong answer.
 */
import { Download, Plus, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import type { CrewMember } from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { EmptyState } from "@/components/gb/empty-state";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AddCrewDrawer } from "./add-crew-drawer";
import { PersonDrawer } from "./person-drawer";
import { ReportsTab, filterPeriods, sortPeriods, type StintFilter } from "./reports-tab";
import { RosterTab, filterRoster, type RosterFilter } from "./roster-tab";
import { csvFilename, downloadCsv, periodsCsv, rosterCsv } from "./roster-csv";
import { usePeriods, useRoster } from "./crew-api";
import {
  applyPatch,
  paramValue,
  readSortDir,
  readSortKey,
  REPORTS_CLEAR,
  ROSTER_CLEAR,
  type SortDir,
  type SortKey,
} from "./crew-url";

const ROSTER_FILTERS: RosterFilter[] = ["all", "valid", "expiring", "missing"];
const STINT_FILTERS: StintFilter[] = ["all", "open", "closed"];

export function CrewScreen() {
  const { projectId } = useProjectContext();
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  /**
   * Set only by "Add & upload cards", and cleared as soon as the drawer opens
   * on it. It is what makes that button differ from "Save without documents":
   * the profile opens with the first requirement's upload form already up,
   * which is what "and scan cards" meant in the design.
   */
  const [uploadFor, setUploadFor] = useState<string | null>(null);

  const roster = useRoster(projectId);
  const periods = usePeriods(projectId);

  const tab = params.get("tab") === "reports" ? "reports" : "roster";
  const filter = pick(params.get("filter"), ROSTER_FILTERS, "all");
  const stint = pick(params.get("stint"), STINT_FILTERS, "all");
  const role = params.get("role") ?? "all";
  const search = params.get("q") ?? "";
  const selectedId = params.get("person");
  const sort = readSortKey(params.get("sort"));
  const dir = readSortDir(params.get("dir"));

  /**
   * Apply a patch to the URL in **one** write, always replacing history.
   *
   * One call, never several — see `crew-url.ts` for why two would clobber each
   * other, and for the tests that hold both rules in place.
   */
  const patchParams = (patch: Record<string, string | null>): void => {
    setParams((prev) => applyPatch(prev, patch), { replace: true });
  };

  /** One key, normalised against that key's own default. */
  const setParam = (key: string, value: string | null): void => {
    patchParams({ [key]: paramValue(key, value) });
  };

  const everyone = useMemo(() => {
    const data = roster.data;
    if (!data) return [] as CrewMember[];
    return [...data.onSite, ...data.onboarding, ...data.offSite];
  }, [roster.data]);

  const selected = everyone.find((p) => p.id === selectedId) ?? null;

  const exportCsv = (): void => {
    if (tab === "reports") {
      if (!periods.data) return;
      downloadCsv(
        csvFilename("crew-onsite-periods"),
        // What is exported is what is on screen. Handing back the unfiltered
        // list from a filtered view is how a compliance pack ends up containing
        // people the sender never meant to include.
        // Order too, not just membership: the rule below is about the CSV
        // agreeing with the view it was taken from, and a reader comparing the
        // two cannot reconcile them if the rows are in a different order.
        periodsCsv(
          sortPeriods(filterPeriods(periods.data, { stint, role, query: search }), {
            sort,
            dir,
          }),
        ),
      );
      return;
    }
    if (!roster.data) return;
    downloadCsv(
      csvFilename("crew-roster"),
      rosterCsv(filterRoster(roster.data, filter, search)),
    );
  };

  const busy = tab === "reports" ? periods.isPending : roster.isPending;
  const failed = tab === "reports" ? periods.error : roster.error;

  return (
    <ContentColumn>
      <Tabs
        variant="underline"
        value={tab}
        onValueChange={(next) => setParam("tab", next === "reports" ? "reports" : null)}
        className="mb-[22px]"
      >
        <TabsList>
          <TabsTrigger value="roster">Roster</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mb-4 flex items-center justify-end gap-2.5">
        <Button variant="secondary" onClick={exportCsv} disabled={busy || Boolean(failed)}>
          <Download size={17} />
          {tab === "reports" ? "Export report" : "Export roster"}
        </Button>
        <Button onClick={() => setAdding(true)}>
          <Plus size={18} />
          Add crew member
        </Button>
      </div>

      {failed ? (
        <EmptyState
          icon={Users}
          title="Couldn’t load the crew"
          description={failed instanceof Error ? failed.message : "Please try again."}
        />
      ) : busy ? (
        <p className="py-10 text-center text-[0.9rem] text-muted">Loading the roster…</p>
      ) : tab === "reports" ? (
        <ReportsTab
          periods={periods.data ?? []}
          stint={stint}
          onStintChange={(next) => setParam("stint", next)}
          role={role}
          onRoleChange={(next) => setParam("role", next)}
          search={search}
          onSearchChange={(next) => setParam("q", next)}
          // One write, not three — see `setParams_`.
          onClear={() => patchParams(REPORTS_CLEAR)}
          sort={sort}
          dir={dir}
          // Both keys in one patch, normalised so the default order leaves
          // nothing in the URL.
          onSortChange={(next: { sort: SortKey; dir: SortDir }) =>
            patchParams({
              sort: paramValue("sort", next.sort),
              dir: paramValue("dir", next.dir),
            })
          }
        />
      ) : roster.data ? (
        <RosterTab
          roster={roster.data}
          filter={filter}
          onFilterChange={(next) => setParam("filter", next)}
          search={search}
          onSearchChange={(next) => setParam("q", next)}
          onClear={() => patchParams(ROSTER_CLEAR)}
          onSelect={(person) => setParam("person", person.id)}
        />
      ) : null}

      <PersonDrawer
        person={selected}
        projectId={projectId}
        issuingBodies={roster.data?.jurisdiction?.docIssuingBodies ?? {}}
        startUploading={selected !== null && uploadFor === selected.id}
        open={selected !== null}
        onClose={() => {
          setParam("person", null);
          setUploadFor(null);
        }}
      />

      <AddCrewDrawer
        projectId={projectId}
        jurisdiction={roster.data?.jurisdiction ?? null}
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(person, andUpload) => {
          setAdding(false);
          // Either way their profile opens — "save without documents" still
          // wants to show what was created rather than dropping the user back
          // on a list to find them in.
          if (andUpload) setUploadFor(person.id);
          setParam("person", person.id);
        }}
      />
    </ContentColumn>
  );
}

function pick<T extends string>(value: string | null, allowed: T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}
